import { AnimatePresence, m } from "framer-motion";
import {useState, useEffect} from "react";
// @mui
import { alpha, styled, useTheme } from "@mui/material/styles";
import { Stack, Divider, Typography, IconButton, Box, Button } from "@mui/material";
// hooks
import useSettings from "../../../hooks/useSettings";
import usePlannerSettings from "../../../contexts/PlannerContext";
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from "../../../utils/cssStyles";
import axios from "../../../utils/axios";
// config
import { NAVBAR, defaultSettings } from "../../../config";
//
import Iconify from "../../Iconify";
import Scrollbar from "../../Scrollbar";
import { varFade } from "../../animate";
//
import ToggleButton from "./ToggleButton";
import SettingFullscreen from "./SettingFullscreen";
import SettingPlannerTool from "./SettingPlannerTool";
import SettingPlannerInstrument from "./SettingPlannerInstrument";
import SettingPlannerAction from "./SettingPlannerAction";
import SettingPlannerSlabs from "./SettingPlannerSlabs";
import SettingPlannerFinishes from "./SettingPlannerFinishes";
import SettingPlannerLines from "./SettingPlannerLines";
//
import { useTranslation } from "react-i18next";
import ComplementoFinishesSelector from "./ComplementoFinishesSelector";
import {FurnitureSelector} from "./FurnitureSelector";
import ToggleComplementiButton from "./ToggleComplementiButton";
import {asyncCanSupportFurnishingsByMaterialId} from "../../../utils/canSupportFurnishings";

// ----------------------------------------------------------------------

const RootStyle = styled(m.div)(({ theme }) => ({
  ...cssStyles(theme).bgBlur({
    color: theme.palette.background.paper,
    opacity: 0.92,
  }),
  top: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  position: "fixed",
  overflow: "hidden",
  width: NAVBAR.BASE_WIDTH,
  flexDirection: "row",
  margin: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  zIndex: theme.zIndex.drawer + 3,
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  boxShadow: `-24px 12px 32px -4px ${alpha(
      theme.palette.mode === "light"
          ? theme.palette.grey[500]
          : theme.palette.common.black,
      0.16
  )}`,
}));

// ----------------------------------------------------------------------

export default function SettingsDrawer(props) {
  const theme = useTheme();
  const {
    themeMode,
    themeLayout,
    themeStretch,
    themeContrast,
    themeDirection,
    themeColorPresets,
    onResetSetting,
  } = useSettings();
  const { complementiAttivi } = props;
  const isDesktop = useResponsive('up', 'lg');
  const { line, oldLine, nonVisibleFacesAvailable } = usePlannerSettings();
  const { t, i18n } = useTranslation();

  const [canSupportFurnishings, setCanSupportFurnishings] = useState(false);
  const [furnitureNavState, setFurnitureNavState] = useState(false);
  const [furnitureResetFn, setFurnitureResetFn] = useState(null);

  const handleFurnitureNavigation = (hasNavigation, resetFn) => {
    setFurnitureNavState(!!hasNavigation);
    setFurnitureResetFn(resetFn ? () => resetFn.resetNavigation : null);
  };
  useEffect(() => {
    let cancelled = false;
    asyncCanSupportFurnishingsByMaterialId(line).then(res => {
      if (cancelled) {
        return
      }
      setCanSupportFurnishings(res);
    })
    return () => {
      cancelled = true;
    }
  }, [line])

  const [accessoryVariantType, setAccessoryVariantType] = useState("");
  const [open, setOpen] = useState(isDesktop ? true : false);
  const [materialName, setMaterialName] = useState("");

  const notDefault =
      themeMode !== defaultSettings.themeMode ||
      themeLayout !== defaultSettings.themeLayout ||
      themeStretch !== defaultSettings.themeStretch ||
      themeContrast !== defaultSettings.themeContrast ||
      themeDirection !== defaultSettings.themeDirection ||
      themeColorPresets !== defaultSettings.themeColorPresets;

  const varSidebar =
      themeDirection !== "rtl"
          ? varFade({
            distance: NAVBAR.BASE_WIDTH,
            durationIn: 0.32,
            durationOut: 0.32,
          }).inRight
          : varFade({
            distance: NAVBAR.BASE_WIDTH,
            durationIn: 0.32,
            durationOut: 0.32,
          }).inLeft;

  const titleStyles = {
    color: theme.palette.primary.main,
  };

  /**
   * This code prevent the scroll of the rest of the page when the drawer is open.
   */
  // useEffect(() => {
  //   if (open) {
  //     document.body.style.overflow = 'hidden';
  //   } else {
  //     document.body.style.overflow = '';
  //   }
  // }, [open]);

  useEffect(() => {
    if (line) {
      if (line === 10) {
        if (oldLine) {
          getSelectedMaterialName(oldLine);
        }
      } else {
        getSelectedMaterialName(line);
      }
    }
  }, [line, oldLine]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getSelectedMaterialName = async (materialId) => {
    try {
      const res = await axios.post(`/planner/materials/${materialId}`);
      setMaterialName(res.data.material.nicename);
    } catch (error) {
      console.log(error);
    }
  };

  return (
      <>
        {!open && (
            <ToggleButton
                open={open}
                notDefault={notDefault}
                onToggle={handleToggle}
            />
        )}

        <AnimatePresence>
          {open && (
              <RootStyle {...varSidebar} sx={{ width: isDesktop ? "420px" : "370px", height: 'calc(99vh - 90px)' }}>
                <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 1,
                    }}
                >
                  <IconButton onClick={handleClose} sx={{ ml: 1 }}>
                    <Iconify icon={"eva:arrow-forward-outline"} width={20} height={20} />
                  </IconButton>
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ py: 2, pr: 1, pl: 2.5 }}
                  >
                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                      {complementiAttivi
                          ? t("accessories_settings.drawer.title")
                          : t("settings.drawer.title")}
                    </Typography>
                  </Stack>
                  {/*{complementiAttivi && (*/}
                  {/* ToggleComplementiButton moved to bottom after Scrollbar */}
                  {/*)}*/}

                  <Divider sx={{ borderStyle: "dashed" }} />

                  <div style={{height: 'calc(99vh - 240px)'}}>
                  <Scrollbar sx={{ flexGrow: 1 }}>
                    <Stack spacing={3} sx={{ p: 3 }}>
                      <Stack spacing={1.5}>
                        <Typography component="h5" variant="h5" sx={titleStyles}>
                          {t("settings.drawer.instruments.title")}
                        </Typography>
                        <SettingPlannerInstrument complementiAttivi={complementiAttivi} />
                      </Stack>

                      <Stack spacing={1.5}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography component="h5" variant="h5" sx={titleStyles}>
                            {complementiAttivi
                                ? t("accessories_settings.drawer.blocks.title")
                                : t("settings.drawer.blocks.title")}
                          </Typography>
                            {complementiAttivi && furnitureNavState ? <Button
                              variant="outlined"
                              size="small"
                              onClick={() => furnitureResetFn && furnitureResetFn()}
                            >
                              {t("dialogs.stepper.back")}
                            </Button> : null}
                          </Box>
                        {complementiAttivi ? (
                            <FurnitureSelector
                              onVariantSelect={(variant) => setAccessoryVariantType(variant)}
                              onNavigationChange={handleFurnitureNavigation}
                            />
                        ) : (
                            <SettingPlannerTool />
                        )}
                      </Stack>

                      {/*{!complementiAttivi && (*/}
                      {/*    <Box sx={{ display: "flex", justifyContent: "center", mb: 2, width: "100%" }}>*/}
                      {/*      <ToggleComplementiButton />*/}
                      {/*    </Box>*/}
                      {/*)}*/}

                      {!complementiAttivi && (
                          <Stack spacing={1.5}>
                            <Typography component="h5" variant="h5" sx={titleStyles}>
                              {t("settings.drawer.main")}
                            </Typography>
                            {t(materialName)}
                          </Stack>
                      )}

                      {(!complementiAttivi || (complementiAttivi && accessoryVariantType)) && (
                          <Stack spacing={1.5}>
                            <Typography component="h5" variant="h5" sx={titleStyles}>
                              {t("settings.drawer.finishes.title")}
                            </Typography>
                            {complementiAttivi ? (
                                <ComplementoFinishesSelector variant={accessoryVariantType} />
                            ) : (
                                <SettingPlannerFinishes material={materialName} />
                            )}
                          </Stack>
                      )}

                      {nonVisibleFacesAvailable && (
                          <Stack spacing={1.5}>
                            <Typography component="h5" variant="h5" sx={titleStyles}>
                              {t("settings.drawer.slabs.title")}
                            </Typography>
                            <SettingPlannerSlabs />
                          </Stack>
                      )}

                      {!complementiAttivi && (
                          <Stack spacing={1.5}>
                            <Typography component="h5" variant="h5" sx={titleStyles}>
                              {t("settings.drawer.lines.title")}
                            </Typography>
                            <SettingPlannerLines />
                          </Stack>
                      )}

                      <Stack spacing={1.5}>
                        <Typography component="h5" variant="h5" sx={titleStyles}>
                          {t("settings.drawer.actions.title")}
                        </Typography>
                        <SettingPlannerAction new={props.new} />
                      </Stack>

                      <Stack spacing={1.5}>
                        <Typography component="h5" variant="h5" sx={titleStyles}>
                          {t("settings.drawer.others.title")}
                        </Typography>
                        <SettingFullscreen />
                      </Stack>
                    </Stack>
                  </Scrollbar>
                  </div>

                  {canSupportFurnishings && (
                      <Box
                          sx={{
                            pt: 2,
                            px: 2,
                            display: "flex",
                            justifyContent: "center",
                          }}
                      >
                        <ToggleComplementiButton />
                      </Box>
                  )}
                </Box>
              </RootStyle>
          )}
        </AnimatePresence>
      </>
  );
}
